import React, { useState } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_STRING } from 'constants/RouteString';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { PAGE_SIZE } from 'constants/Common';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppSearch from '@tenant/core/AppSearchBar';
import useGetListAppPackage from 'redux/slices/appPackage/hooks/useGetListAppPackage';
import { useSelector } from 'react-redux';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  inputFormatDateTime,
} from '@tenant/utility/constants/default';
import { formatDateFromISO } from '@tenant/utility/helper/DateHelper';
import useModal from '@tenant/utility/hooks/useModal';
import { deepClone } from '@mui/x-data-grid/utils/utils';
import useDeleteAppPackage from 'redux/slices/appPackage/hooks/useDeleteAppPackage';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import DeleteAppPackageAction from '../Common/DeleteAppPackageAction';
import { debounce } from '@tenant/utility/helper/Utils';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQueryClient } from '@tanstack/react-query';
import queryKeyAppPackage from 'redux/slices/appPackage/constants/queryKeyAppPackage';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';

const AppPackageTable = () => {
  const nextTick = useNextTick();
  const canDelete = useAllowedAccess([AuthorizePermission.APP_PACKAGE_DELETE]);
  const queryClient = useQueryClient();
  const { list, filter } = useSelector((s) => s.appPackage);
  const [pageIndex, setPageIndex] = useState(
    filter?.pageIndex ?? DEFAULT_PAGE_INDEX,
  );
  const [searchTerm, setSearchTerm] = useState(filter?.searchTerm ?? '');
  const [isSearchTermChanged, setIsSearchTermChanged] = useState(false);
  const [selected, setSelected] = useState();
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;
  const { isLoading, isRefetching, refetch } = useGetListAppPackage({
    searchTerm,
    pageIndex,
    pageSize,
  });
  const deleteMutate = useDeleteAppPackage();
  const totalCount = list?.TotalCount ?? 0;
  const navigate = useNavigate();
  const { visible, onClose, onShow } = useModal();
  const rows = list?.Items ?? [];
  const loading = isLoading || isRefetching;

  const debounceSetSelected = debounce((value) => {
    setSelected(value);
  }, 500);

  const clearSearchTerm = async () => {
    setSearchTerm('');
    await nextTick();
    refetch();
  };

  const onChangeSearch = (e) => {
    setSearchTerm(e.target.value);
    setPageIndex(DEFAULT_PAGE_INDEX);
    setIsSearchTermChanged(true);
  };

  const onPageChange = (value) => {
    setPageIndex(value);
  };

  const goToAdd = () => {
    navigate(ROUTE_STRING.APP_PACKAGE.ADD);
  };

  const handleCloseDelete = () => {
    onClose();
    debounceSetSelected();
  };
  const handleShowDelete = (item) => {
    setSelected(deepClone(item));
    onShow();
  };

  const refreshTableAction = () => {
    if (pageIndex === DEFAULT_PAGE_INDEX) {
      refetch();
      return;
    }

    queryClient.removeQueries(
      queryKeyAppPackage.getList(JSON.stringify(DEFAULT_PAGE_INDEX, pageSize)),
    );
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const columns = [
    {
      field: 'Name',
      headerName: 'Name',
      width: 400,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Link to={ROUTE_STRING.APP_PACKAGE.VIEW.replace(':id', params.row.Id)}>
          {params.value}
        </Link>
      ),
    },
    {
      field: 'Version',
      headerName: 'Version',
      width: 125,
      sortable: false,
    },
    {
      field: 'CreatedAt',
      headerName: 'Created At',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box
          component={'span'}
          sx={{ width: '100%', display: 'block' }}
          className='MuiDataGrid-cellContent'
        >
          {params.value
            ? formatDateFromISO(params.value, inputFormatDateTime)
            : ''}
        </Box>
      ),
    },
    {
      field: 'Action',
      headerName: '',
      width: 50,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          sx={{ minWidth: 150 }}
          key='action--view'
          label='View'
          showInMenu
          onClick={() => {
            navigate(ROUTE_STRING.APP_PACKAGE.LIST + '/' + params.row.Id);
          }}
        />,
        <GridActionsCellItem
          key='action--delete'
          label='Delete'
          showInMenu={canDelete}
          onClick={() => {
            handleShowDelete(params.row);
          }}
        />,
      ],
    },
  ];

  return (
    <>
      <AppsHeader>
        <Box
          flex={1}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <AppSearch
              isClearable
              searchValue={searchTerm}
              onClearSearch={clearSearchTerm}
              iconPosition='right'
              id='outlined-adornment-weight'
              size='small'
              overlap={false}
              onChange={onChangeSearch}
              onBlur={() => {
                isSearchTermChanged && refetch();
                setIsSearchTermChanged(false);
              }}
            ></AppSearch>
          </Box>
          <AllowedAccess
            isPageFailed
            permissionIds={[AuthorizePermission.APP_PACKAGE_CREATE]}
          >
            <Button variant='outlined' onClick={goToAdd}>
              Add New
            </Button>
          </AllowedAccess>
        </Box>
      </AppsHeader>

      <DeleteAppPackageAction
        open={visible}
        loading={deleteMutate.isPending}
        handleCloseDelete={handleCloseDelete}
        handleSubmitDelete={() => {
          handleCloseDelete();
          refetch();
        }}
        selected={selected}
      />

      <Box style={{ height: 'auto', width: '100%' }}>
        <DataGrid
          hideFooter
          autoHeight
          disableColumnSelector
          disableColumnMenu
          rows={rows}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
        />
      </Box>

      <FooterWrapper className='footer'>
        <Box justifyContent={'center'} className='footerContainer'>
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE) || 1}
            page={pageIndex}
            onChange={(e, v) => onPageChange(v)}
            showFirstButton
            showLastButton
          />
          <Typography>Total: {totalCount || 0}</Typography>
          <Tooltip title='Refresh'>
            <IconButton
              disabled={isRefetching}
              size='small'
              sx={{ ml: 2 }}
              onClick={refreshTableAction}
            >
              <RefreshIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      </FooterWrapper>
    </>
  );
};

export default AppPackageTable;
